import Header from "@components/layout/header";
import Footer from "@components/layout/footer";
import { loadTranslation } from "@lib/utils";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { t } from "@lingui/macro";

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translation = await loadTranslation(
    locale,
    process.env.NODE_ENV == "production"
  );

  return { props: { translation } };
};

const Term = () => {
  const title = t`PDPA - Lumi News`;
  const description = t`Understand how we use the personal information we may collect from you.`;

  return (
    <>
      <NextSeo title={title} description={description} />

      <Header />

      <main className="bg-off-white">
        <div className="mx-auto max-w-7xl py-8 px-4 font-sen">
          <p>
            <strong>Privacy Policy</strong>
          </p>
          <p>
            <strong>Introduction</strong>
          </p>
          <p>
            In compliance with the Personal Data Protection Act 2010 (&ldquo;the
            Act&rdquo;) and its regulations, this notice is issued to all our
            valued users and/or prospective users. This Notice will inform you
            of your rights with regards to your personal data that has been
            and/or will be, collected and processed by us.
          </p>
          <p>
            <strong>
              <em>Pengenalan</em>
            </strong>
          </p>
          <p>
            <em>
              Bagi mematuhi Akta Perlindungan Data Peribadi (&ldquo;Akta&rdquo;)
              dan peraturannya, notis ini dikeluarkan untuk rujukan pengguna
              sedia ada dan/atau bakal pengguna kami. Notis ini memaklumkan
              kepada anda tentang hak-hak berkaitan data peribadi anda yang
              telah dan/atau akan dikumpul dan diproses oleh pihak kami.
            </em>
          </p>
          <p>
            <strong>Collection of personal data</strong>
          </p>
          <p>
            We have collected and will collect your personal data to be
            processed by us and/or on your behalf in the course of your present
            or future dealings with LUMI GLOBAL SDN BHD (&ldquo;we&rdquo;,
            &ldquo;our&rdquo;, &ldquo;us&rdquo; or &ldquo;Company&rdquo;). Your
            personal data enables us to achieve the &ldquo;Purpose&rdquo; herein
            that is in connection with our business.
          </p>
          <p>
            <strong>
              <em>Pengumpulan data peribadi</em>
            </strong>
          </p>
          <p>
            <em>
              Kami telah mengumpul dan akan mengumpul data peribadi anda untuk
              diproses oleh pihak kami dan/atau menjadi wakil bagi pihak anda
              untuk sebarang urusan semasa atau akan datang melibatkan LUMI
              GLOBAL SDN BHD (&ldquo;kami&rdquo; atau &ldquo;Syarikat&rdquo;).
              Data peribadi anda membolehkan kami mencapai &ldquo;Tujuan&rdquo;
              kami sebagaimana yang dinyatakan di sini yang berhubung kait
              dengan perniagaan kami.
            </em>
          </p>
          <p>
            <strong>Description of personal data</strong>
          </p>
          <ol>
            <li>Email address</li>
            <li>IP address</li>
            <li>
              Personally Identifiable Information (PII) defined by international
              laws or standards&nbsp;
            </li>
          </ol>
          <p>
            <strong>
              <em>Huraian data peribadi</em>
            </strong>
          </p>
          <ol>
            <li>
              <em>Emel&nbsp;</em>
            </li>
            <li>
              <em>Alamat IP&nbsp;</em>
            </li>
            <li>
              <em>
                Maklumat Pengenalan Diri (PII) yang ditakrifkan oleh
                undang-undang atau piawaian antarabangsa
              </em>
            </li>
          </ol>
          <p>
            <strong>Purposes for collection of personal data</strong>
          </p>
          <p>
            We will use the user&rsquo;s personal data for the following
            purposes including but not limited to:
          </p>
          <ol>
            <li>
              Facilitate logins where a person can use the app and all its
              features as a signed-in user. Effective 19 November 2022, we no
              longer gather personal data for OTP codes. Users can easily log in
              using single sign-on (SSO) method.
            </li>
            <li>
              Providing users with our services including receiving opt-in push
              notifications of news content, the ability to access articles in
              the app, bookmarking articles, viewing recently read articles,
              changing preferred languages, and customising the app to the
              user&rsquo;s preferences.
            </li>
            <li>
              Operating and improving the app and our services; understanding a
              user&rsquo;s preferences and their interests in order to cater a
              customised environment according to the user&rsquo;s needs.
            </li>
            <li>
              Data analytics, internal business, administrative and
              troubleshooting purposes.
            </li>
          </ol>
          <p>
            <strong>
              <em>Tujuan pengumpulan data peribadi</em>
            </strong>
          </p>
          <p>
            <em>
              Kami akan menggunakan data peribadi pengguna untuk tujuan berikut,
              termasuk tetapi tidak terhad kepada:
            </em>
          </p>
          <ol>
            <li>
              <em>
                Mudahkan log masuk di mana seseorang boleh menggunakan aplikasi
                dan semua cirinya sebagai pengguna yang log masuk. Berkuat kuasa
                19 November 2022, kami tidak lagi mengumpulkan data peribadi
                untuk kod OTP. Pengguna boleh log masuk dengan mudah menggunakan
                kaedah daftar diri tunggal (SSO).
              </em>
            </li>
            <li>
              <em>
                Menyediakan pengguna dengan perkhidmatan kami termasuk menerima
                notifikasi pilih-masuk (opt-in) melibatkan kandungan berita,
                kemampuan untuk mengakses artikel dalam aplikasi, menanda
                artikel, melihat artikel terkini yang dibaca, menukar pilihan
                bahasa, dan menyesuaikan aplikasi mengikut kehendak pengguna.
              </em>
            </li>
            <li>
              <em>
                Mengoperasikan dan menambah baik aplikasi dan perkhidmatan kami;
                memahami kehendak dan kecenderungan pengguna bagi mewujudkan
                persekitaran yang sesuai berdasarkan keperluan pengguna.&nbsp;
              </em>
            </li>
            <li>
              <em>
                Analisis data, urusan dalaman perniagaan, pengurusan dan
                penyelesaian masalah.
              </em>
            </li>
          </ol>
          <p>
            <strong>Sources of personal data</strong>
          </p>
          <ol>
            <li>
              The Company obtains a user&rsquo;s personal data after a user
              downloads the application and goes through the sign up process
              where they are required to choose a social login, or previously
              input a mobile phone number, to sign up or sign in.
            </li>
            <li>
              When the user visits our website. Anonymous user data is collected
              by third-party vendor Google Analytics, for the purpose of
              tracking website activity and understanding general user
              behaviour. User data may also be collected by other selected
              third-party vendors and/or business partners.&nbsp;
            </li>
            <li>
              When the user participates in any feedback surveys or marketing
              efforts for the purpose of improving our services.
            </li>
            <li>
              When users contact us through verbal or written communications and
              any other instances where users provide information to us from
              time to time.
            </li>
          </ol>
          <p>
            <strong>
              <em>Sumber Data Peribadi</em>
            </strong>
          </p>
          <ol>
            <li>
              <em>
                Syarikat memperoleh data peribadi pengguna selepas pengguna
                memuat turun aplikasi dan menjalani proses pendaftaran yang
                memerlukan pengguna untuk menggunakan ciri log masuk sosial,
                ataupun sebelum ini, memerlukan nombor telefon bimbit mereka
                untuk mendaftar atau log masuk.&nbsp;
              </em>
            </li>
            <li>
              <em>
                Apabila pengguna melayari laman web kami. Data pengguna yang
                tidak dikenali dikumpulkan oleh pihak ketiga iaitu Google
                Analytics, bagi menjejaki aktiviti laman web dan memahami
                tingkah laku pengguna secara umum. Data pengguna juga mungkin
                dikumpul oleh pihak ketiga lain (vendor) dan/atau rakan niaga
                terpilih.&nbsp;
              </em>
            </li>
            <li>
              <em>
                Apabila pengguna menyertai sebarang kaji selidik maklum balas
                atau usaha pemasaran yang dijalankan bagi menambah baik
                perkhidmatan kami.
              </em>
            </li>
            <li>
              <em>
                Apabila pengguna menghubungi kami melalui komunikasi secara
                verbal atau bertulis dan lain-lain, yang memerlukan mereka
                memberi maklumat kepada kami dari semasa ke semasa.
              </em>
            </li>
          </ol>
          <p>
            <strong>Disclosure of personal data</strong>
          </p>
          <p>We may disclose your personal data internally to:</p>
          <ol>
            <li>Our company, business partners or subsidiaries.</li>
            <li>
              Any vendor, agent, data processor, telecommunications company,
              legal, accounting, technology outsourcing, and administrative
              services that are connected to business operations and our
              services provided to you.
            </li>
            <li>
              Government or regulatory bodies in any jurisdiction or any person
              to whom we are legally obliged to disclose data.
            </li>
            <li>
              Any other third-party that has been requested or authorized by you
              for the Purposes mentioned above.
            </li>
          </ol>
          <p>
            <strong>
              <em>Pendedahan data peribadi</em>
            </strong>
          </p>
          <p>
            <em>
              Kami boleh mendedahkan data peribadi anda untuk kegunaan dalaman
              kepada:
            </em>
          </p>
          <ol>
            <li>
              <em>Syarikat kami, rakan niaga atau anak syarikat</em>
            </li>
            <li>
              <em>
                Mana-mana pembekal (vendor), ejen, pemproses data, syarikat
                perkhidmatan telekomunikasi, perundangan, perakaunan,
                penyumberan luar teknologi, dan pengurusan yang mempunyai hubung
                kait dengan operasi perniagaan dan perkhidmatan kami kepada
                anda.
              </em>
            </li>
            <li>
              <em>
                Badan-badan kerajaan atau pengawalseliaan dalam mana-mana bidang
                kuasa atau mana-mana individu yang boleh mendedahkan data secara
                sah di sisi undang-undang.&nbsp;&nbsp;
              </em>
            </li>
            <li>
              <em>
                Mana-mana pihak ketiga yang telah diminta atau diberi kuasa oleh
                anda sebagaimana Tujuan yang dinyatakan di atas.
              </em>
            </li>
          </ol>
          <p>
            Parties will treat your personal data as confidential, in accordance
            with this Privacy Policy and with all applicable Data Protection
            legislation and will process such personal data only for the
            Purposes and within the terms set out herein.&nbsp;
          </p>
          <p>
            <em>
              Pihak yang berkenaan akan meletakkan data peribadi anda sebagai
              sulit, bertepatan dengan Dasar Privasi ini dan semua undang-undang
              yang melibatkan Perlindungan Data serta akan memproses data
              peribadi tersebut hanya untuk Tujuan dan terma yang telah
              dinyatakan di sini.&nbsp;
            </em>
          </p>
          <p>
            We take every measure to provide a comparable level of protection
            for personal data should the information be processed by a Vendor.
            LUMI GLOBAL SDN BHD is committed to complying with the Personal Data
            Protection Act 2010, in particular, its policies as well as
            corresponding guidelines and orders.
          </p>
          <p>
            <em>
              Kami telah mengambil semua langkah sewajarnya bagi memastikan
              tahap perlindungan data peribadi diberikan dengan sebaiknya,
              sekiranya maklumat tersebut diproses oleh Vendor. LUMI GLOBAL SDN
              BHD komited dalam mematuhi Akta Perlindungan Data Peribadi 2010,
              khususnya melibatkan dasar, panduan dan arahan yang berkaitan.
            </em>
          </p>
          <p>
            <strong>Security of personal data</strong>
          </p>
          <p>
            LUMI GLOBAL SDN BHD uses strict procedures and security features to
            prevent unauthorised access wherever possible. Personal data
            provided to the Company via our website or the Lumi application is
            protected during transit using encryption such as Transport Layer
            Security (TLS). When personal data is stored by us, we use computer
            systems with limited access. Data stored in cloud services is in
            encrypted form including when we utilize third-party storage. The
            level of security of personal data which is kept in a non-electronic
            environment is also treated with strict procedures and means.
          </p>
          <p>
            <strong>
              <em>Keselamatan data peribadi</em>
            </strong>
          </p>
          <p>
            <em>
              LUMI GLOBAL SDN BHD menggunakan prosedur dan ciri keselamatan yang
              ketat untuk menghalang akses yang tidak dibenarkan. Data peribadi
              yang diberikan kepada Syarikat melalui laman web kami atau
              aplikasi Lumi adalah dilindungi semasa transit yang menggunakan
              penyulitan seperti Transport Layer Security (TLS). Apabila kami
              menyimpan data peribadi, kami menggunakan sistem komputer dengan
              akses yang terhad. Data yang disimpan dalam perkhidmatan awan
              adalah dalam bentuk yang disulitkan termasuk ketika kami
              menggunakan penyimpanan pihak ketiga. Tahap keselamatan data
              peribadi yang telah disimpan dalam persekitaran bukan elektronik
              juga diletakkan dengan prosedur dan kaedah yang ketat.
            </em>
          </p>
          <p>
            <strong>Use of cookies</strong>
          </p>
          <p>
            The cookies used by our website are solely associated with anonymous
            users and their computers and do not provide personal data on users.
            Some cookies are used by third parties to provide our Company with
            data on the effectiveness of its engagements and promotions. The
            cookies used by the Website do not in any way collect personal data
            that could be used to identify a specific user. In addition, cookies
            are of a temporary nature, used solely to improve the efficiency of
            the last transmission. Users may configure their web browser to
            notify them of the reception of cookies and to prevent their
            installation on their computer.
          </p>
          <p>
            <strong>
              <em>Penggunaan cookies</em>
            </strong>
          </p>
          <p>
            <em>
              Cookies yang digunakan oleh laman web kami hanya melibatkan
              pengguna tidak dikenali dan komputer mereka, dan tidak memberikan
              data peribadi pengguna kepada kami. Sesetengan cookies yang
              digunakan oleh pihak ketiga bertujuan menyediakan Syarikat kami
              dengan data keberkesanan penggunaan dan promosi. Selain itu,
              cookies adalah bersifat sementara, yang digunakan hanya untuk
              menambah baik kecekapan transmisi terakhir. Pengguna boleh
              mengkonfigurasi pelayar laman web mereka untuk memaklumkan tentang
              penerimaan cookies dan menghalang pemasangannya di komputer
              mereka.
            </em>
          </p>
          <p>
            <strong>Transfer of Personal Data</strong>
          </p>
          <p>
            We may transfer user data to be processed outside Malaysia by our
            vendors and will provide a comparable level of protection for
            personal data.&nbsp;
          </p>
          <p>
            <strong>
              <em>Pemindahan Data peribadi</em>
            </strong>
          </p>
          <p>
            <em>
              Kami boleh memindahkan data pengguna untuk diproses di luar
              Malaysia oleh vendor kami dan akan menyediakan tahap perlindungan
              yang sebaiknya untuk data peribadi.
            </em>
          </p>
          <p>
            <strong>Data subject&rsquo;s obligations</strong>
          </p>
          <p>
            It is necessary for the Company to collect and to retain user data
            and therefore obligatory for users to supply data to us in the most
            accurate manner. If clients do not provide us with personal data, we
            are unable to process the data on the user&rsquo;s behalf, for the
            Purpose stated above, or effectively render our services to our
            users, and all relationships created or to be created between us
            shall then be terminated and cease to be in effect immediately.
          </p>
          <p>
            <strong>
              <em>Tanggungjawab subjek data</em>
            </strong>
          </p>
          <p>
            <em>
              Adalah wajar bagi Syarikat untuk mengumpulkan dan mengekalkan data
              pengguna, dan adalah menjadi tanggung jawab pengguna untuk
              memberikan data yang tepat kepada kami. Sekiranya pengguna tidak
              memberikan data peribadi, kami tidak dapat memproses data tersebut
              bagi pihak pengguna, bagi Tujuan yang telah dinyatakan di atas,
              atau membolehkan kami menyampaikan perkhidmatan yang berkesan
              kepada pengguna, dan semua perkara yang wujud atau akan diwujudkan
              melibatkan pihak kami akan dibatalkan dan diberhentikan dengan
              serta-merta.
            </em>
          </p>
          <p>
            <strong>The rights of data subject</strong>
          </p>
          <ol>
            <li>
              <strong>To delete or request access to personal data</strong>
            </li>
          </ol>
          <p>
            <span>
              You have the right to delete or request for access to personal
              data processed by us with reasonable notice. Deletion or access
              requests can be made through by emailing us at{" "}
            </span>
            <a
              href="mailto:hello@luminews.my"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>hello@luminews.my</span>
            </a>
            <span> with your name and user ID or email included.</span>
          </p>
          <ol>
            <li>
              <strong>To request correction of personal data</strong>
            </li>
          </ol>
          <p>
            Users have the rights to request for correction and/or update of
            your personal data&nbsp;
          </p>
          <p>
            that is inaccurate, incomplete, or outdated. Correction requests can
            be made via emailing to the email address above with your name and
            email address or user ID included.
          </p>
          <ol>
            <li>
              <strong>To limit processing of your personal data</strong>
            </li>
          </ol>
          <p>
            Users are entitled to limit our processing of personal data by
            expressly withdrawing the consent given previously, in each case,
            including for direct marketing purposes subject to any application
            legal restrictions, contractual conditions, and within a reasonable
            time period. The Company may refuse to comply with a data access
            request or a data correction request and shall, by notice in
            writing, inform you of our refusal and the reasons for our decision.
          </p>
          <p>
            <strong>
              <em>Hak subjek data</em>
            </strong>
          </p>
          <ol>
            <li>
              <strong>
                <em>Untuk padam atau memohon akses kepada data peribadi</em>
              </strong>
            </li>
          </ol>
          <p>
            <em>
              Anda mempunyai hak untuk memadam atau memohon akses kepada data
              peribadi yang telah diproses oleh kami dengan notis yang
              munasabah. Permohonan pemadaman atau pengaksesan boleh dibuat
              melalui e-mel di{" "}
            </em>
            <a
              href="mailto:hello@luminews.my"
              target="_blank"
              rel="noopener noreferrer"
            >
              <em>hello@luminews.my</em>
            </a>
            <em> dengan menyertakan nama dan nombor ahli atau emel anda.</em>
          </p>
          <ol>
            <li>
              <strong>
                <em>Untuk memohon pembetulan data peribadi</em>
              </strong>
            </li>
          </ol>
          <p>
            <em>
              Pengguna mempunyai hak untuk membetulkan dan/atau mengemaskini
              data peribadi yang tidak tepat, tidak lengkap atau sudah lama.
              Permohonan pembetulan boleh dibuat melalui e-mel di{" "}
            </em>
            <a
              href="mailto:hello@luminews.my"
              target="_blank"
              rel="noopener noreferrer"
            >
              <em>hello@luminews.my</em>
            </a>
            <em> dengan menyertakan nama dan nombor ahli atau emel anda.</em>
          </p>
          <ol>
            <li>
              <strong>
                <em>Untuk mengehadkan pemprosesan data peribadi anda</em>
              </strong>
            </li>
          </ol>
          <p>
            <em>
              Pengguna dibenarkan untuk mengehadkan pemprosesan data peribadi
              oleh kami dengan menyatakan penarikan semula kebenaran yang telah
              diberikan sebelum ini, dalam setiap kes, termasuk bagi tujuan
              pemasaran terus, tertakluk kepada sebarang sekatan undang-undang
              yang berkaitan, syarat kontrak, dan dalam tempoh yang munasabah.
              Syarikat boleh menolak untuk memenuhi permohonan pengaksesan dan
              pembetulan data melalui notis bertulis, bagi memaklumkan kepada
              anda tentang penolakan dan sebab-sebab keputusan tersebut dibuat.
            </em>
          </p>
          <p>
            <strong>Changes to the Privacy Policy</strong>
          </p>
          <p>
            We reserve the right to amend this Privacy Policy from time to time
            without prior notice. We advise that you check our app and website (
            <a
              href="https://www.luminews.my"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.luminews.my
            </a>
            ) for the Privacy Policy on a regular basis.&nbsp;
          </p>
          <p>
            By continuing your usage of our application, our website and/or
            services, you hereby agree to the Privacy Policy and any amendments
            made to it.
          </p>
          <p>
            <strong>
              <em>Perubahan dasar privasi</em>
            </strong>
          </p>
          <p>
            <em>
              Kami berhak mengubah Dasar Privasi ini dari semasa ke semasa tanpa
              sebarang makluman awal. Kami menyarankan anda untuk memeriksa
              aplikasi dan laman web (
            </em>
            <a
              href="https://www.luminews.my"
              target="_blank"
              rel="noopener noreferrer"
            >
              <em>www.luminews.my</em>
            </a>
            <em>) secara kerap mengenai Dasar Privasi tersebut.</em>
          </p>
          <p>
            <em>
              Dengan meneruskan penggunaan aplikasi, laman web dan/atau
              perkhidmatan kami, anda dengan ini bersetuju dengan Dasar privasi
              yang ditetapkan dan sebarang pembetulan yang dibuat.
            </em>
          </p>
        </div>
        <Footer />
      </main>
    </>
  );
};

export default Term;
